import { useCallback, useEffect, useState } from 'react';
import {
  dispatch,
  ErrorVariants,
  getExtraDetails,
  getSelectedItemIds,
  getUserData,
  getWorkspace,
  removeSearchKey,
  RouteScheduleResponse,
  showToast,
  updateExtraDetails,
  useAppSelector,
  useAssignRouteToCustomerMutation,
  useLazyGetRouteScheduleQuery,
} from '@zonofi/zono-money-store';
import { FIELD_OPS_ROUTES } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import { useDebounceSearch, useLoadAllApiDataV2 } from '../../../hooks';
import { isEmpty } from 'lodash';

export const useAssignRoutes = (
  swApiPaths?: (string | ((routeId: string) => string))[]
) => {
  const navigate = useNavigate();

  const customerDetails = useAppSelector(getExtraDetails);
  const teamMemberId = useAppSelector(getUserData).inviteId;
  const workspace = useAppSelector(getWorkspace);
  const selectedRouteId = useAppSelector(getSelectedItemIds);

  const { debouncedSearchValue } = useDebounceSearch();
  const [showSearch, setShowSearch] = useState(false);

  const [getFieldOpsRouteListAPI, getFieldOpsRouteListAPIResponse] =
    useLazyGetRouteScheduleQuery();
  const [assignRouteToCustomer, assignRouteToCustomerResponse] =
    useAssignRouteToCustomerMutation();

  useEffect(() => {
    return () => {
      dispatch(removeSearchKey());
    };
  }, []);

  const fetchFieldOpsRouteList = useCallback(
    (params: { pageNo: number; maxPageSize: number; refresh?: boolean }) => {
      if (workspace?.id) {
        getFieldOpsRouteListAPI({
          sellerWorkspaceId: workspace?.id,
          teamMemberId: teamMemberId ?? '',
          pageNo: params.refresh ? 1 : params.pageNo,
          pageSize: params.maxPageSize,
        });
      }
    },
    [teamMemberId]
  );

  const { data: routeData, isPageLoading } = useLoadAllApiDataV2<
    RouteScheduleResponse,
    RouteScheduleResponse[]
  >({
    fetchDataApiCall: fetchFieldOpsRouteList,
    apiResponse: getFieldOpsRouteListAPIResponse,
    transformer: {
      getData: (res) => res, // array directly
      getPagination: (res) => ({
        hasNextPage: res.length === 100, // infer from count
      }),
    },
    uniqueKey: 'routeId',
    maxPageSize: 100,
    dependencies: [],
    searchText: debouncedSearchValue,
    searchPaths: ['route.name'],
  });

  const handleAssignRouteById = () => {
    if (
      !selectedRouteId?.length ||
      !customerDetails?.customerId ||
      !workspace?.id
    )
      return;

    assignRouteToCustomer({
      body: {
        customerIds: [customerDetails.customerId],
      },
      groupId: selectedRouteId[0],
      sellerWorkspaceId: workspace?.id,
    });
  };

  useEffect(() => {
    if (
      assignRouteToCustomerResponse.isSuccess &&
      assignRouteToCustomerResponse.data
    ) {
      if (
        assignRouteToCustomerResponse.data?.customerGroup &&
        isEmpty(assignRouteToCustomerResponse.data?.errorCustomers)
      ) {
        dispatch(
          updateExtraDetails({
            ...customerDetails,
            routeCustomerGroup: {
              routeId: assignRouteToCustomerResponse?.data?.customerGroup?.id,
              routeName:
                assignRouteToCustomerResponse?.data?.customerGroup?.name,
              customerGroupMembersCount:
                assignRouteToCustomerResponse?.data?.customerGroup
                  ?.CustomerGroupMembers.length || 0,
            },
          })
        );
        const resolvedPaths =
          swApiPaths?.map((p) =>
            typeof p === 'function' ? p(selectedRouteId[0]) : p
          ) ?? [];
        if (resolvedPaths.length)
          navigator.serviceWorker.controller?.postMessage({
            type: 'RECORD_MUTATION',
            listPaths: resolvedPaths,
          });
        navigate(FIELD_OPS_ROUTES.ASSIGN_BEAT_PLAN);
        return;
      } else {
        dispatch(
          showToast({
            message: 'This customer is already assigned to another route',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );

        setTimeout(() => {
          navigate(FIELD_OPS_ROUTES.VIEW_CUSTOMER_LIST);
        }, 200);
      }
    }

    if (assignRouteToCustomerResponse.isError) {
      dispatch(
        showToast({
          message: 'Failed to assign routes',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [assignRouteToCustomerResponse]);

  return {
    routeData,
    isPageLoading,
    showSearch,
    setShowSearch,
    handleAssignRouteById,
    isAssignLoading: assignRouteToCustomerResponse.isLoading,
    customerName: workspace?.spaceName,
    selectedRouteId,
  };
};
